import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { InventoryItem, InventoryOverviewItem } from '@/supabase/inventory/types/inventory';
import { isLowStock } from '@/supabase/inventory/types/inventory';
// 컴포넌트의 유일한 non-UI import 허용(태스크 브리프 3-C) — 검색 필터가 norm_name과
// 동일 정규화 규칙으로 query를 비교해야 한다.
import { normalizeItemName } from '@/supabase/inventory/normalizeItemName';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { InfoTip } from './InfoTip';
import { InventoryRow } from './InventoryRow';

interface InventoryTableProps {
  items: InventoryOverviewItem[];
  isLoading: boolean;
  // 초기 로드 실패 상태(codexloop R1 Fix D) — items가 빈 배열인 "정말 등록된 상품이 없음"과
  // "로드에 실패해 아직 아무것도 못 받아온 상태"를 구분해 표시한다. 둘 다 optional로 둔 이유는
  // 이 컴포넌트를 쓰는 다른 자리(있다면)가 재고 조회 실패를 신경 안 써도 되게 하기 위함.
  hasError?: boolean;
  onRetry?: () => void;
  // PR3 리뷰 수용: 입출고/조정 콜백은 InventoryOverviewItem을 받는다(모달 배지가 가용 판정에
  // reserved를 써야 함 — MovementModalContainer 참고). 삭제는 reserved가 필요 없어 그대로 둔다.
  onOpenMovement: (item: InventoryOverviewItem) => void;
  onOpenAdjust: (item: InventoryOverviewItem) => void;
  // 수정(상품 정보)·삭제는 reserved가 필요 없어 InventoryItem 그대로.
  onOpenEdit: (item: InventoryItem) => void;
  onDelete: (item: InventoryItem) => void;
  // 일괄 삭제 — 선택 상태는 이 컴포넌트가 소유(콜로케이션)하고, 확인 다이얼로그 오픈만
  // 페이지에 위임한다(컨테이너 ref는 페이지 소유 — 단일 삭제 onDelete와 동일 배선).
  onBulkDelete: (items: InventoryItem[]) => void;
  // PR3: 예약 ⓘ 툴팁 문구 조립용 원시값(RPC 그대로) — 문자열은 여기서 조립(콜로케이션,
  // CLAUDE.md 규율 6). 둘 다 number/boolean 원시값이라 memo 계약(규율 5)과 무관.
  windowDays: number;
  scanTruncated: boolean;
  // UI 폴리시: 컨트롤 행(검색·부족만 보기) 우측에 붙일 페이지 소유 액션(엑셀 일괄등록 버튼).
  // 이전엔 페이지가 버튼 하나를 위한 고아 행을 따로 그려 수직 리듬이 끊겼다 — 슬롯으로 받아
  // 같은 행에 정렬한다(dumb 컴포넌트 유지: 노드만 받고 핸들러/상태는 페이지 소유).
  headerAction?: ReactNode;
}

const LOW_STOCK_TOGGLE_ID = 'inventory-low-stock-only';

export function InventoryTable({
  items,
  isLoading,
  hasError,
  onRetry,
  onOpenMovement,
  onOpenAdjust,
  onOpenEdit,
  onDelete,
  onBulkDelete,
  windowDays,
  scanTruncated,
  headerAction,
}: InventoryTableProps) {
  // 콜로케이션(CLAUDE.md 규율 6): 검색어·부족만 토글·일괄 삭제 선택은 이 컴포넌트에서만
  // 읽으므로 여기 둔다.
  const [query, setQuery] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  // 선택 = id Set. items를 미러하지 않는다 — 유효 선택은 렌더 시 현재 필터 목록과의 교집합
  // 파생(아래 selectedItems)이라, 삭제·refresh·필터 변경으로 사라진 행은 자동으로 선택에서
  // 빠진다(별도 클리어 effect 불필요). Set에 남은 죽은 id는 효력이 없고, id는 IDENTITY라
  // 재사용되지 않아 새 행에 오선택될 수도 없다.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(new Set());

  const filteredItems = useMemo(() => {
    const normQuery = normalizeItemName(query);
    return items.filter((item) => {
      // PR3: 부족 판정 기준을 stock(현재고)→available(가용)로 전환 — 예약분은 실제로 팔 수 없는
      // 수량이므로 부족 여부는 가용 기준이어야 정확하다(라벨 "부족만 보기"는 그대로).
      // PR7-2: 판정을 isLowStock 헬퍼로 이관(동작 동등: 기존 `가용 > 기준` 통과 ↔ !isLowStock)
      // — KPI '부족·품절' 카운트와 단일 판정을 공유해야 목록 필터 개수와 숫자가 항상 일치한다.
      if (lowStockOnly && !isLowStock(item)) return false;
      if (normQuery && !item.norm_name.includes(normQuery)) return false;
      return true;
    });
  }, [items, query, lowStockOnly]);

  // 유효 선택 = 지금 보이는(필터·검색 통과) 행과의 교집합 — 삭제 대상은 항상 눈에 보이는
  // 것만(검색으로 가려진 행이 몰래 지워지는 사고 차단). 버튼 개수·다이얼로그 목록·헤더
  // 체크박스 판정 전부 이 파생값 기준.
  const selectedItems = useMemo(
    () => filteredItems.filter((item) => selectedIds.has(item.id)),
    [filteredItems, selectedIds],
  );
  const allSelected = filteredItems.length > 0 && selectedItems.length === filteredItems.length;

  // 행 체크박스 토글 — 안정 콜백(deps 없음, 함수형 업데이터)이라 memo 자식(InventoryRow)
  // 계약을 지킨다.
  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // 헤더 체크박스 — 전체 선택 시 해제는 "보이는 행"만 제거(가려진 선택 잔존은 어차피 무효),
  // 선택은 보이는 행 전부 추가.
  const handleToggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (filteredItems.length > 0 && filteredItems.every((item) => next.has(item.id))) {
        filteredItems.forEach((item) => next.delete(item.id));
      } else {
        filteredItems.forEach((item) => next.add(item.id));
      }
      return next;
    });
  }, [filteredItems]);

  return (
    // 툴바(카운트·검색·부족만·엑셀)를 목록 카드 내부 헤더로 내부화(UI 폴리시 3차) — 이전엔
    // 툴바가 미매칭 패널과 목록 카드 사이에 소속 없이 떠 있어 "검색이 어느 구획 것인지"
    // 모호했다. overflow-hidden은 마지막 행 hover 배경이 카드 라운드 밖으로 새는 것 방지.
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        {/* 예약 정의·차감 동작 설명은 상시 캡션 대신 해당 컬럼 헤더 ⓘ 툴팁으로(카피 개편
            2026-07-16 — 상시 노출 결정(2026-07-13)을 오너가 뒤집음: 장황해서 안 읽힘). */}
        <p className="text-sm font-medium text-slate-700">전체 {items.length.toLocaleString('ko-KR')}개</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {/* 선택이 있을 때만 등장하는 파괴 액션 — red는 이 화면에서 파괴 전용 어휘(행 휴지통과
              동일). Button은 memo 자식이 아니라 인라인 클로저 허용. */}
          {selectedItems.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onBulkDelete(selectedItems)}
            >
              선택 {selectedItems.length.toLocaleString('ko-KR')}개 삭제
            </Button>
          )}
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="상품명 검색"
            aria-label="상품명 검색"
            className="sm:w-56"
          />
          <Label htmlFor={LOW_STOCK_TOGGLE_ID} className="flex cursor-pointer items-center gap-2 text-sm font-normal text-slate-600">
            <Checkbox
              id={LOW_STOCK_TOGGLE_ID}
              checked={lowStockOnly}
              onCheckedChange={(checked) => setLowStockOnly(checked === true)}
              // 이 레포 shadcn Checkbox 기본 체크 색(bg-primary)은 tailwind.config에 토큰이 없는 죽은
              // 클래스라 명시 지정 필수(ProductList.tsx 선례).
              className="data-[state=checked]:border-slate-900 data-[state=checked]:bg-slate-900 data-[state=checked]:text-white"
            />
            부족만 보기
          </Label>
          {headerAction}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2 px-4 py-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : hasError && items.length === 0 ? (
        <div className="p-10 text-center">
          <p className="break-keep text-sm text-slate-500">목록을 불러오지 못했습니다</p>
          {onRetry && (
            // isLoading일 때 disabled(codexloop R2 Fix 4) — useInventory.refresh가 이제 빈 목록
            // 재시도에서도 isLoading을 올리므로, 그 신호를 받아 중복 클릭을 막는다.
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry} disabled={isLoading}>
              다시 시도
            </Button>
          )}
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center">
          <p className="break-keep text-sm text-slate-500">등록된 상품이 없습니다</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="p-10 text-center">
          <p className="break-keep text-sm text-slate-500">조건에 맞는 상품이 없습니다</p>
        </div>
      ) : (
        <div>
          {/* min-w-[678px]: 고정 폭 합(544px)+상품명 최소폭(134px) 보장 — 이보다 좁은 화면은
              카드 내부(overflow-auto 래퍼)에서 가로 스크롤(상품명이 22px로 붕괴하는 것 방지,
              codexloop 폴리시 R1). 숫자 컬럼 폭은 '10,000'급 5자리까지 수용.
              고정 폭 합 544px = 체크박스 40 + 판매가 96 + 현재고 80 + 예약 64 + 가용 80 +
              관리 184 (일괄 삭제 체크박스 컬럼 +40px, 2026-07-17).
              678px < 페이지 컨테이너(max-w-3xl 768px)이므로 데스크톱에서는 가로 스크롤이
              생기지 않는다 — 레이아웃 개편(2026-07-16, 오너: 휴지통이 스크롤 뒤에 숨는 문제)
              으로 부족 기준·등록일 컬럼 삭제(저빈도 메타 — 부족 기준은 수정 모달에서 관리).
              관리 폭 184px = 기존 150px + 수정 아이콘(28px)+gap(6px).
              현재고 폭 80px = 텍스트(36px)+ⓘ(14px)+gap — 카피 개편 2026-07-16로 헤더에 ⓘ 툴팁
              추가(예약은 2글자라 w-16에 ⓘ까지 수용).
              table-fixed + 전 컬럼 명시 폭(UI 폴리시 2차 — 오너 실물 지적): auto 레이아웃은
              내용량 따라 잉여 폭을 전 컬럼에 흩뿌려 숫자 컬럼들 사이·등록일↔관리 사이에
              들쭉날쭉한 공백을 만든다. 고정 폭이면 잉여는 상품명(유일한 무폭 컬럼)이 전부
              흡수하고 데이터 컬럼 간격이 결정적으로 균일해진다.
              정렬은 2존 리듬: 상품명(좌) | 나머지 전부 우측(가용 뱃지 포함 — 혼자 중앙이면
              우/우/중/우 지그재그로 스캔 라인이 끊긴다).
              헤더 톤은 명시 지정 — table.tsx 기본 text-muted-foreground는 이 레포 tailwind
              토큰에 없는 죽은 클래스라 본문색을 상속해 헤더가 데이터보다 무거워 보였다. */}
          <Table className="min-w-[678px] table-fixed">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10 pl-4 pr-0">
                  {/* 전체 선택 = 지금 보이는(필터 통과) 행 기준. 일부 선택은 indeterminate —
                      이 레포 Checkbox 래퍼는 indeterminate에도 체크 글리프를 그리므로 톤을
                      낮춘 slate-400 배경으로 '부분'을 구분한다(글리프 교체는 공용 ui 수정이라
                      회피). 죽은 토큰(bg-primary) 회피 명시 지정은 부족만 보기와 동일. */}
                  <Checkbox
                    checked={allSelected ? true : selectedItems.length > 0 ? 'indeterminate' : false}
                    onCheckedChange={handleToggleAll}
                    aria-label="전체 선택"
                    className="data-[state=checked]:border-slate-900 data-[state=checked]:bg-slate-900 data-[state=checked]:text-white data-[state=indeterminate]:border-slate-400 data-[state=indeterminate]:bg-slate-400 data-[state=indeterminate]:text-white"
                  />
                </TableHead>
                <TableHead className="whitespace-nowrap pl-3 text-xs font-medium text-slate-400">상품명</TableHead>
                <TableHead className="w-24 whitespace-nowrap text-right text-xs font-medium text-slate-400">판매가</TableHead>
                <TableHead className="w-20 whitespace-nowrap text-right text-xs font-medium text-slate-400">
                  <span className="inline-flex items-center gap-1">
                    현재고
                    <InfoTip ariaLabel="현재고 차감 기준">
                      주문을 입금 완료·출고 완료로 바꾸면 자동 차감되고, 입금 전으로 되돌리면
                      복원됩니다. 입금 후 주문 수정도 반영되지만, 입금 뒤에 등록한 상품은 소급
                      차감되지 않습니다
                    </InfoTip>
                  </span>
                </TableHead>
                <TableHead className="w-16 whitespace-nowrap text-right text-xs font-medium text-slate-400">
                  <span className="inline-flex items-center gap-1">
                    예약
                    <InfoTip ariaLabel="예약 집계 기준">
                      <p>입금 전 주문 수량 (최근 {windowDays}일~미래 30일 기준)</p>
                      {scanTruncated && <p>주문이 많아 최근 2,000건만 집계</p>}
                    </InfoTip>
                  </span>
                </TableHead>
                <TableHead className="w-20 whitespace-nowrap text-right text-xs font-medium text-slate-400">가용</TableHead>
                <TableHead className="w-[184px] whitespace-nowrap pr-4 text-right text-xs font-medium text-slate-400">관리</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => (
                // 콜백은 그대로 전달(새 클로저 없음) — InventoryRow가 React.memo라 여기서 인라인
                // 클로저를 만들면 memo가 매 렌더 무효화된다(태스크 브리프 3-C, CLAUDE.md 규율 5).
                // isSelected는 원시 boolean이라 선택이 바뀐 행만 재렌더.
                <InventoryRow
                  key={item.id}
                  item={item}
                  isSelected={selectedIds.has(item.id)}
                  onToggleSelect={handleToggleSelect}
                  onOpenMovement={onOpenMovement}
                  onOpenAdjust={onOpenAdjust}
                  onOpenEdit={onOpenEdit}
                  onDelete={onDelete}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
